// src/oura/screens/Visibility.tsx
//
// Visibility, one screen for both roles: staff at
// /oura/businesses/:id/questions inside the business chrome, the merchant at
// /oura/questions. How many of the questions name the business, on which
// engine, over time and by type, then the questions themselves, the rivals
// named on them and the sites the answers read.
//
// The merchant gets the same report without the operator's tools: no
// refresh, no instrument labels, no fix links, no question-set swaps.
//
// The engine, tab, filter, page and open question live in the URL beside
// `?scan=`, so a link reproduces the view and the back button closes a
// question. A past scan withholds the writable ids, and every control that
// writes renders nothing without one.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangleIcon, DownloadIcon, HistoryIcon, InfoIcon, RefreshCwIcon, EyeIcon, UsersIcon } from "lucide-react";
import {
    getTrackerSummary,
    setFixtureScansPublished,
    setQuestionVersionPublished,
    VERSION_PUBLISH_LIVE,
    VERDICT_FIX_LIVE,
    type AeoBusinessCompetitor,
    type AeoScoreHistoryEntry,
    type AeoTrackerSummaryTask,
} from "@service/aeo";
import {
    Bento,
    EngineLogo,
    FoldCard,
    FoldScope,
    ListRow,
    PageActions,
    PageHead,
    PageSentence,
    PillButton,
    REDUCED_MOTION,
    Seg,
    Surface,
    TYPE,
    useMediaQuery,
    type SegItem,
} from "@oura/ui";
import { CLIENT_HINT, type View } from "../nav";
import { MERCHANT_V2 } from "../merchantV2";
import { partialScanNote } from "../components/GatedReason";
import { useOuraRole } from "../role";
import type { GscState } from "../gscState";
import { cn } from "../lib/cn";
import { pageHref, shortDate } from "../lib/oura";
import { downloadQuestionsCsv } from "../lib/questionsCsv";
import { VISIBILITY_CHECK_ORDER, type VisibilityCheckName } from "../audit";
import { useGoogleAiRefresh } from "../hooks/useGoogleAiRefresh";
import { useStageRefresh, type StageRefreshJob } from "../hooks/useRefreshJob";
import { ANALYSE_REFRESH, SEARCH_REFRESH, STAGE_RERUN_LIVE } from "../refreshJobs";
import { CardRefresh, RefreshConfirmDialog, RefreshProgress } from "../components/RefreshControl";
import { buildTrend, buildVisModel, visibilityInputsFromAssessment, type LatestAssessment, type VisibilityInputs } from "../visibility/fromLive";
import {
    isKindFilter,
    kindFilter,
    kindsOf,
    lostFilter,
    questionFilters,
    rivalsOn,
    trendDelta,
    VIS_ENGINES,
    type EngineFilter,
    type QuestionFilter,
    type TrendDelta,
    type VisEngine,
    type VisModel,
    type VisRow,
} from "../visibility/model";
import { dayMonth, monthName } from "../visibility/format";
import { useQuestionAnswers } from "../visibility/useQuestionAnswers";
import { ScanAuditBadge } from "../components/ScanAuditBadge";
import { VisTiles } from "../components/visibility/VisTiles";
import { NamedOverTime } from "../components/visibility/NamedOverTime";
import { TypeGrid } from "../components/visibility/TypeGrid";
import { ReportCard, SEG_FILL_ON_PHONE, VersionMarker, type ReportTab } from "../components/visibility/ReportCard";
import { QuestionsTab } from "../components/visibility/QuestionsTab";
import { SearchesEmptyNotice, UncoveredSearches } from "../components/visibility/UncoveredSearches";
import { CompetitorsTab } from "../components/visibility/CompetitorsTab";
import { usePriorScan } from "../visibility/usePriorScan";
import { useQuestionTrendScans } from "../visibility/useQuestionTrendScans";
import { cellTrend, newSinceFor } from "../visibility/questionTrend";
import { SourcesTab } from "../components/visibility/SourcesTab";
import { QuestionHistory, SetChangeBanner } from "../components/visibility/QuestionHistory";
import { changeAtScan, measureNoteDates, questionKey, questionSetVersions, questionsOnlyTrend, scanBeforeVersion, versionName, versionOfScan, type QuestionSetVersion } from "../visibility/questionSets";
import { useScanQuestions } from "../visibility/useScanQuestions";
import { fixtureAtScan, withFixturePublished } from "../visibility/fixtures";
import { QuestionDrawer, type NamedElsewhereBlock } from "../components/visibility/QuestionDrawer";
import { CitesDrawer } from "../components/visibility/CitesDrawer";
import { VisibilityTabs } from "../merchant/VisibilityTabs";

const TABS: ReportTab[] = ["questions", "competitors", "sources"];

/** How often a scan whose Google AI pages are still being read is re-fetched. */
const GOOGLE_PENDING_POLL_MS = 45_000;

/** An engine's name where the phone gives it a quarter of the width. */
// "Google AI", not "Google": on a phone the bare word read as Google search.
const SHORT_ENGINE: Partial<Record<VisEngine, string>> = { ai_overview: "Google AI" };

/**
 * What the staff Visibility page is for. Fixed, as the client's is (#4026
 * C-FE-6): the line under a title describes the page, and the figures go in
 * the status row below it.
 */
const STAFF_VISIBILITY_DESCRIPTION = "Where AI assistants and Google name this business, and who they name instead.";

/** Today's figures: the count, the move, and what to look at. */
function sentence(model: VisModel, staff: boolean, delta: TrendDelta | null): string {
    const h = model.headline;
    if (h.of <= 0) return h.absent ?? "";
    const parts = [`${h.named} of ${h.of} questions name ${staff ? "this business" : "you"}.`];
    if (delta?.comparable) {
        const since = staff ? dayMonth(delta.since) : monthName(delta.since);
        parts.push(delta.value === 0
            ? `No change since ${since}.`
            : `${delta.value > 0 ? "Up" : "Down"} ${Math.abs(delta.value)} since ${since}.`);
    }
    if (h.weakest) parts.push(staff ? `${h.weakest.label} names it least.` : `${h.weakest.label} names you least.`);
    if (!staff && h.benchmark !== null) parts.push(`A typical business we measure: ${h.benchmark}.`);
    return parts.join(" ");
}

/**
 * The rivals a scan recorded with no visibility block to count them against
 * (`businessAudit.competitors`). Staff only: the list the old Overview's
 * competitor analysis showed, so it keeps a home when there are no answers.
 */
function FoundCompetitors({ competitors }: { competitors: AeoBusinessCompetitor[] }) {
    return (
        <FoldCard
            alwaysOpen
            id="found-competitors"
            title="Competitors found on this scan"
            icon={{ icon: UsersIcon, area: "ai" }}
            right={`${competitors.length} ${competitors.length === 1 ? "rival" : "rivals"}`}
        >
            <div>
                {competitors.map((c) => {
                    const href = c.domain.trim() ? pageHref(c.domain) : null;
                    // `queries: null` is a join that found nothing: not scored,
                    // never "named on 0".
                    const reach = c.queries === null ? "Not scored in this run" : `Named on ${c.queries} ${c.queries === 1 ? "question" : "questions"}`;
                    return (
                        <ListRow
                            key={c.name}
                            title={c.name}
                            sub={[href ? c.domain.trim() : "Named in answers only", c.note.trim()].filter(Boolean).join(" · ")}
                            meta={reach}
                        />
                    );
                })}
            </div>
        </FoldCard>
    );
}

/** A live business whose scan carries no visibility block, or a sample without one. */
function EmptyVisibility({
    staff,
    businessName,
    capturedAt,
    competitors,
}: {
    staff: boolean;
    businessName: string;
    capturedAt: string;
    /** Staff: the rivals the scan found, listed under the empty state. */
    competitors: AeoBusinessCompetitor[];
}) {
    // A capture date is the proof a scan happened, so with one we say what
    // that scan did and nothing about what came before it. Without one there
    // is no assessment at all, the only case "not measured yet" can claim.
    const title = capturedAt ? "No visibility result from the last scan" : "Not measured yet";
    const body = capturedAt
        ? `The scan on ${shortDate(capturedAt)} read the website but returned no AI visibility numbers.`
        : `We have not asked the AI assistants about ${staff ? businessName : "your business"} yet.`;
    return (
        <FoldScope value={staff ? "staff-visibility" : "merchant-visibility"}>
            {!staff && <PageHead over={businessName} title="Visibility" />}
            {!staff && MERCHANT_V2 && <VisibilityTabs value="questions" />}
            <div className="space-y-5">
                <Surface className="items-center py-12 text-center">
                    <EyeIcon aria-hidden size={24} className="text-gray-300" />
                    <p className={cn(TYPE.tt, "m-0 mt-3 text-gray-900")}>{title}</p>
                    <p className={cn(TYPE.tb, "m-0 mt-1 max-w-md text-gray-500")}>{body}</p>
                </Surface>
                {staff && competitors.length > 0 && <FoundCompetitors competitors={competitors} />}
            </div>
        </FoldScope>
    );
}

export function Visibility({
    businessId,
    businessName,
    capturedAt,
    assessment,
    history,
    fixture,
    readAssessmentId,
    writableAssessmentId,
    writableBusinessId,
    nextCheckAt,
    nextScanAt,
    searchHref,
    gscState,
    scanControl,
    onOpenScan,
    onBackToLatest,
    onRefreshed,
    onNavigate,
    onOpenTask,
}: {
    businessId: string;
    businessName: string;
    /** The scan's capture date, from the workspace snapshot. */
    capturedAt: string;
    /** The scan on screen, latest or past. Live businesses only. */
    assessment?: LatestAssessment | null;
    /** Every terminal scan, for the history behind Named over time. */
    history?: AeoScoreHistoryEntry[];
    /** A sample business's live-shaped payload. */
    fixture?: VisibilityInputs | null;
    /** The scan whose answers and captures are read. Kept on a past scan: reading is not writing. */
    readAssessmentId?: string;
    /** Withheld on a past scan, and every write control goes with it. */
    writableAssessmentId?: string;
    writableBusinessId?: string;
    /** The merchant's next AI check, from their client page. */
    nextCheckAt?: string | null;
    /** Staff: the next scheduled scan (`next_auto_scan_at`). Null when none is scheduled. */
    nextScanAt?: string | null;
    /** The Search performance screen, for both roles. */
    searchHref?: string;
    /** What the Search Console connection is, as Overview reads it; undefined until the catalog arrives (#4235). */
    gscState?: GscState;
    /** The client's past-scan picker, in the page's header. Staff have theirs in the business chrome. */
    scanControl?: ReactNode;
    /**
     * Puts one scan on screen for the whole page (the workspace's `?scan=`),
     * the newest included. A version picked on Named over time opens its
     * latest scan through it, so the table below shows that version's
     * questions. Live businesses only: a sample keeps its pick here.
     */
    onOpenScan?: (assessmentId: string) => void;
    /** Back to the newest scan. */
    onBackToLatest?: () => void;
    onRefreshed?: () => void | Promise<void>;
    onNavigate: (view: View) => void;
    onOpenTask: (taskId: string) => void;
}) {
    const staff = useOuraRole() === "oura-staff";
    const merchantScanNote = !staff && MERCHANT_V2 && assessment
        ? partialScanNote({ errorCode: assessment.error_code, notice: assessment.notice_detail, status: assessment.status })
        : null;
    const reducedMotion = useMediaQuery(REDUCED_MOTION);
    const [params, setParams] = useSearchParams();
    const reportRef = useRef<HTMLDivElement>(null);
    const [keySlot, setKeySlot] = useState<HTMLElement | null>(null);

    // Scans staff published or hid on this page, until the history is read again.
    const [publishedNow, setPublishedNow] = useState<Record<string, string | null>>({});
    const [publishTick, setPublishTick] = useState(0);
    // A sample's earlier scan, picked on the chart: the sample has no
    // workspace scan to open, so it stands in for `?scan=` here.
    const [fixtureScan, setFixtureScan] = useState<string | null>(null);
    useEffect(() => setFixtureScan(null), [fixture]);
    const inputs = useMemo<VisibilityInputs | null>(() => {
        const read: VisibilityInputs | null = fixture
            ? fixtureAtScan(withFixturePublished(fixture), fixtureScan)
            : assessment ? visibilityInputsFromAssessment(assessment, { capturedAt, history: history ?? [] }) : null;
        if (!read) return null;
        const shown = read.history
            .map((h) => (h.assessment_id in publishedNow ? { ...h, published_at: publishedNow[h.assessment_id] } : h))
            // The client's history never carries a held scan (the backend
            // leaves it out); this only keeps a sample, or a scan hidden a
            // moment ago, out of the client's versions too.
            .filter((h) => staff || h.published_at !== null);
        return { ...read, history: shown };
        // publishTick: a sample re-reads what this browser saved.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixture, fixtureScan, assessment, capturedAt, history, publishedNow, publishTick, staff]);

    /**
     * Publish or hide one question version: every scan of it, in one backend
     * call, so it is never left half shown. A version is shown to the client
     * exactly when its scans are; there is no second switch. The client is
     * told only about the newest scan, and only when staff tick it. Answers
     * with the reason it was refused, or null.
     */
    const setVersionPublished = useCallback(
        async (version: QuestionSetVersion, on: boolean, notify: boolean): Promise<string | null> => {
            const ids = version.scans.map((sc) => sc.assessmentId);
            const res = fixture
                ? await setFixtureScansPublished(ids, on)
                : await setQuestionVersionPublished({ businessId, questionSetId: version.setId, assessmentId: ids[ids.length - 1], published: on, notify });
            if (!res.ok) return res.message;
            const now = new Date().toISOString();
            setPublishedNow((was) => ({
                ...was,
                ...Object.fromEntries(version.scans.map((sc) => [sc.assessmentId, on ? sc.publishedAt ?? now : null])),
            }));
            setPublishTick((t) => t + 1);
            return null;
        },
        [fixture, businessId],
    );

    // The plan's tasks, for the Fix column (staff). A past scan has no
    // writable assessment, and no fix is offered against old numbers.
    const [tasks, setTasks] = useState<AeoTrackerSummaryTask[] | null>(null);
    useEffect(() => {
        setTasks(null);
        if (!staff || fixture || !writableAssessmentId) return;
        let live = true;
        void getTrackerSummary(writableAssessmentId).then((s) => { if (live) setTasks(s?.tasks ?? null); });
        return () => { live = false; };
    }, [staff, fixture, writableAssessmentId]);

    const model = useMemo(
        () => (inputs && (fixture || inputs.vis) ? buildVisModel(inputs, staff ? inputs.fixTasks ?? tasks : null) : null),
        [inputs, fixture, staff, tasks],
    );
    const answers = useQuestionAnswers(fixture ? undefined : readAssessmentId, inputs?.grounding?.instrument);
    /** The last scan on the same questions, for the Competitors tab's "vs" column. */
    const priorScan = usePriorScan({ inputs, fixture: !!fixture, enabled: params.get("tab") === "competitors" });
    /** The earlier scans the Questions tab's trend arrows compare against (#4125). */
    const trendTab = params.get("tab");
    const trendScans = useQuestionTrendScans({
        inputs,
        fixture: !!fixture,
        enabled: trendTab !== "competitors" && trendTab !== "sources",
    });

    /**
     * The question sets the history records (v1, v2, …), both roles: the
     * version tabs on the chart, the change banner, the version on the New
     * chip and Question history (`?sets=`). Read-only, so the client sees
     * the same history staff do. Their questions are read only while the
     * drawer is open, the scan on screen starts a new set, or to mark New
     * against the set before this one.
     */
    const versions = useMemo(() => (inputs ? questionSetVersions(inputs.history) : []), [inputs]);
    const currentVersion = versionOfScan(versions, inputs?.assessmentId)?.n ?? null;
    // The newest scored scan, and its version: "Current" on the chart, which
    // ever scan is on screen.
    const newestScan = useMemo(
        () => versions.flatMap((v) => v.scans.map((sc) => ({ ...sc, n: v.n }))).sort((a, b) => Date.parse(b.capturedAt) - Date.parse(a.capturedAt))[0] ?? null,
        [versions],
    );
    const latestVersion = newestScan?.n ?? currentVersion;
    const onNewest = !newestScan || newestScan.assessmentId === inputs?.assessmentId;
    // Picking a version on the chart puts its latest scan on screen, so the
    // table, the type grid and the tiles show that version's questions
    // (Jack, 22 Oct 2026). The version already on screen stays as it is.
    const backToNewest = fixture ? () => setFixtureScan(null) : onBackToLatest;
    const canPickVersion = staff && (!!fixture || !!onOpenScan);
    const pickVersion = (n: number) => {
        const v = versions.find((x) => x.n === n);
        if (!v || n === currentVersion) return;
        const target = v.scans[v.scans.length - 1].assessmentId;
        // The newest scan's version clears the pick (`?scan=`), so the page
        // keeps following the latest scan when the next one lands.
        if (target === newestScan?.assessmentId && backToNewest) backToNewest();
        else if (fixture) setFixtureScan(target);
        else onOpenScan?.(target);
    };
    // On an earlier scan the chart still runs to the newest one, so every
    // version stays there to pick, the way back included.
    const chartModel = useMemo(
        () => (model && inputs && canPickVersion && !onNewest && newestScan
            ? { ...model, trend: buildTrend({ ...inputs, capturedAt: newestScan.capturedAt }) }
            : model),
        [model, inputs, canPickVersion, onNewest, newestScan],
    );
    const setChange = useMemo(
        () => (inputs ? changeAtScan(versions, inputs.history, inputs.assessmentId) : null),
        [versions, inputs],
    );
    // The last scan of the set before the one on screen: what "New in vN" is
    // read against, on every scan of vN rather than only its first.
    const prevSetScanId = useMemo(() => {
        // The scan just before this version began, on another set (#4583: a
        // set asked again later is one version, so "the previous version's last
        // scan" can come after this one).
        const mine = currentVersion === null ? null : versions.find((v) => v.n === currentVersion) ?? null;
        return scanBeforeVersion(versions, mine)?.assessmentId ?? null;
    }, [versions, currentVersion]);
    const historyOpen = params.get("sets") !== null && versions.length > 0;
    // The pair the drawer shows: the set picked (`?sets=`) and the one it is
    // compared with (`?vs=`, else the set before it). Only those two scans are
    // read, so opening the drawer costs two requests however long the history.
    const historyPicked = Number(params.get("sets")) || currentVersion || versions.length;
    const historyPair = useMemo(() => {
        const b = versions.find((v) => v.n === historyPicked) ?? versions[versions.length - 1] ?? null;
        const earlier = b ? versions.filter((v) => v.n < b.n) : [];
        const vs = Number(params.get("vs"));
        const a = earlier.find((v) => v.n === vs) ?? earlier[earlier.length - 1] ?? null;
        return { a, b };
    }, [versions, historyPicked, params]);
    const setScanIds = useMemo(() => {
        const ids = new Set<string>();
        if (historyOpen && historyPair.b) {
            ids.add(historyPair.b.scans[0].assessmentId);
            if (historyPair.a) ids.add(historyPair.a.scans[historyPair.a.scans.length - 1].assessmentId);
        }
        if (setChange) {
            ids.add(setChange.previousScanId);
            ids.add(setChange.to.scans[0].assessmentId);
        }
        if (prevSetScanId) ids.add(prevSetScanId);
        return Array.from(ids);
    }, [historyOpen, historyPair, setChange, prevSetScanId]);
    const setScans = useScanQuestions({
        ids: setScanIds,
        inputs,
        model,
        fixture: !!fixture,
        enabled: historyOpen || !!setChange || !!prevSetScanId,
    });

    /**
     * Google's AI pages still being re-read after the scan (ourteam-backend#5619):
     * re-fetch the scan every 45 s until the block is gone, then stop. Only the
     * latest scan is polled: a past scan is not re-read, and its writable id is
     * withheld. The ref keeps the interval on the newest refresh callback.
     */
    const refreshRef = useRef(onRefreshed);
    useEffect(() => { refreshRef.current = onRefreshed; });
    const rereading = model?.googlePending?.rereading === true;
    useEffect(() => {
        if (!rereading || !writableAssessmentId || fixture) return;
        const timer = window.setInterval(() => { void refreshRef.current?.(); }, GOOGLE_PENDING_POLL_MS);
        return () => window.clearInterval(timer);
    }, [rereading, writableAssessmentId, fixture]);

    /** Reading Google's AI Overviews on demand (#3922): staff, live, writable. */
    const canReadGoogleAi = staff && !fixture && !!writableBusinessId;
    const googleAi = useGoogleAiRefresh({ businessId: writableBusinessId, enabled: canReadGoogleAi, onSettled: onRefreshed });
    /** One Search Console pull feeds the uncovered searches and Search performance (#3907). */
    const searchJob = useStageRefresh({ spec: SEARCH_REFRESH, assessmentId: writableAssessmentId, businessId, onSettled: onRefreshed });
    /** The free recompute over stored answers (#3908): rivals and sources. */
    const analyseJob = useStageRefresh({ spec: ANALYSE_REFRESH, assessmentId: writableAssessmentId, businessId, onSettled: onRefreshed });
    const canStage = (job: StageRefreshJob) => STAGE_RERUN_LIVE && staff && !!writableAssessmentId && !job.absent;

    /* --------------------------------------------------------------------- *
     * URL state
     * --------------------------------------------------------------------- */

    const update = useCallback((patch: Record<string, string | null>, push = false) => {
        const next = new URLSearchParams(params);
        Object.keys(patch).forEach((k) => {
            const v = patch[k];
            if (v === null) next.delete(k);
            else next.set(k, v);
        });
        setParams(next, { replace: !push });
    }, [params, setParams]);

    const scrollToReport = () =>
        reportRef.current?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });

    if (!model || !inputs) {
        return (
            <EmptyVisibility
                staff={staff}
                businessName={businessName}
                capturedAt={capturedAt}
                competitors={inputs?.auditCompetitors ?? []}
            />
        );
    }

    // Only an engine with a verdict per question can filter the page.
    const engineParam = params.get("engine");
    const engine: EngineFilter = VIS_ENGINES.find((e) => e === engineParam && model.engines[e].perQuestion) ?? "all";
    const tabParam = params.get("tab");
    const tab: ReportTab = TABS.find((t) => t === tabParam) ?? "questions";
    const filters = questionFilters(model, engine, params.get("filter") ?? "all");
    const activeFilter = filters.find((f) => f.key === params.get("filter")) ?? filters[0];
    const page = Math.max(1, Number.parseInt(params.get("page") ?? "1", 10) || 1);
    const openRow = model.rows.find((r) => String(r.id) === params.get("q")) ?? null;
    // A source stays in the URL under a question opened from it, so closing
    // the question goes back to the source rather than to the page.
    const source = model.sources.rows.find((r) => r.domain === params.get("source")) ?? null;
    // On the first scan of a new question set there is no like-for-like
    // change to report: the banner splits the jump instead.
    const delta = setChange ? null : trendDelta(model.trend, "all");
    const kinds = kindsOf(model.rows, VISIBILITY_CHECK_ORDER);
    const rivals = rivalsOn(model, engine);
    /**
     * The engine a type-grid cell replaced, while the type filter it set is
     * on: leaving that filter puts the engine back, so a look at one cell
     * does not leave the whole page on another engine.
     */
    const cellFrom: EngineFilter | null = isKindFilter(activeFilter.key)
        ? (["all", ...VIS_ENGINES] as EngineFilter[]).find((e) => e === params.get("from")) ?? null
        : null;

    const setEngine = (e: EngineFilter) => update({ engine: e === "all" ? null : e, from: null, page: null });
    /** Leaving a cell's filter by any route puts back the engine it replaced. */
    const leaveCell = (): Record<string, string | null> =>
        cellFrom !== null ? { engine: cellFrom === "all" ? null : cellFrom, from: null } : { from: null };
    const setFilter = (f: QuestionFilter) => update({
        filter: f === "all" ? null : f,
        page: null,
        ...(f !== activeFilter.key ? leaveCell() : {}),
    });
    const openQuestion = (row: VisRow) => update({ q: String(row.id), source: null }, true);
    const openFromSource = (row: VisRow) => update({ q: String(row.id) }, true);
    const toTable = (patch: Record<string, string | null>) => {
        update({ tab: null, page: null, ...patch }, true);
        scrollToReport();
    };
    const onCell = (kind: VisibilityCheckName, e: EngineFilter) => {
        const from = cellFrom ?? (e !== engine ? engine : null);
        toTable({ engine: e === "all" ? null : e, filter: kindFilter(kind), from: from === null || from === e ? null : from });
    };

    const engineSeg: SegItem<EngineFilter>[] = [
        {
            key: "all",
            label: <><span className="sm:hidden">All</span><span className="hidden sm:inline">All AIs</span></>,
        },
        ...VIS_ENGINES.filter((e) => model.engines[e].perQuestion).map((e) => ({
            key: e,
            // The logo is part of the label here, not the Seg's own slot,
            // which hides it on a phone: there the logo is what tells three
            // short words apart.
            label: (
                <>
                    <EngineLogo engine={e} size={14} />
                    <span className="sm:hidden">{SHORT_ENGINE[e] ?? model.engines[e].label}</span>
                    <span className="hidden sm:inline">{model.engines[e].label}</span>
                </>
            ),
        })),
    ];
    const seg = engineSeg.length > 2 && (
        <Seg<EngineFilter> ariaLabel="Engine" items={engineSeg} value={engine} onChange={setEngine} className={SEG_FILL_ON_PHONE} />
    );
    const headSentence = sentence(model, staff, delta);

    // Question sets. The set each chart point was asked on, by its date.
    const dateVersion = new Map(inputs.history.map((h) => [h.captured_at, versionOfScan(versions, h.assessment_id)?.n ?? null]));
    const versionAt = (date: string | null) => (date ? dateVersion.get(date) ?? null : null);
    // How ChatGPT was asked on each scan, so the chart can break the line where that changed.
    const dateMethod = new Map(inputs.history.map((h) => [h.captured_at, h.visibility?.engines.chatgpt?.instrument ?? null]));
    const methodAt = (date: string | null) => (date ? dateMethod.get(date) ?? null : null);
    // The merchant's chart labels each point by its version, numbered by
    // question set as the staff page and the scan picker number it (#4657),
    // and its line breaks only where the questions changed: where, how or by
    // which model a scan was asked is a note under the chart, not a new line.
    const merchantSets = !staff && MERCHANT_V2;
    // `versionAt` already counts only what the client sees: on a non-staff view
    // `inputs.history` has its held scans filtered out above, a staff-token "View
    // as the merchant" included, so the chart and the picker (clientScans) agree.
    const scanAt = merchantSets ? versionAt : undefined;
    const shownModel = chartModel ?? model;
    const merchantChart = merchantSets ? { ...shownModel, trend: questionsOnlyTrend(shownModel.trend, inputs.history) } : null;
    const notedOn = merchantChart ? measureNoteDates(merchantChart.trend, inputs.history) : [];
    const openHistory = (n: number | null, vs: number | null = null) =>
        update({ sets: String(n ?? currentVersion ?? versions.length), vs: vs === null ? null : String(vs), q: null, source: null }, true);
    // From the chart: a set against the one before it, named in the link so
    // it reads as the comparison it is. v1 has nothing before it.
    const openVersionHistory = (n: number) => openHistory(n, n > 1 ? n - 1 : null);
    // With a set before this one, New is read against that set's last scan on
    // every scan of this set, so "v3" stays on the questions v3 brought in (it
    // shows on a sample business too). Nothing is marked while that scan
    // loads or when it cannot be read: the scan-to-scan comparison would
    // cross the set change. Nothing either when every question is new: a
    // whole new set has no contrast to mark, and the banner says so.
    const prevSetScan = prevSetScanId ? setScans[prevSetScanId] : null;
    const setNewFor = (() => {
        if (!prevSetScan) return null;
        const asked = new Set(prevSetScan.queries.map((q) => questionKey(q.text)));
        const fresh = model.rows.filter((r) => !asked.has(r.key)).length;
        if (fresh === 0 || fresh === model.rows.length) return null;
        const since = dayMonth(prevSetScan.capturedAt) || "an earlier scan";
        return (row: VisRow) => (asked.has(row.key) ? null : since);
    })();
    const trendRight = staff
        ? [`Checked ${shortDate(model.capturedAt)}`, nextScanAt ? `Next scan ${shortDate(nextScanAt)}` : null].filter(Boolean).join(" · ")
        : nextCheckAt
            ? `Next check ${shortDate(nextCheckAt)}`
            : `Checked ${shortDate(model.capturedAt)}`;

    const tabHint: Record<ReportTab, string> = {
        // Staff only: the merchant's rows each carry a View link instead.
        questions: staff ? "Click a row for the answers" : "",
        // Under one engine the counts are that engine's readings only, so the
        // hint names the engine and its own count (usability F4).
        competitors: !staff ? "" : engine === "all"
            ? `Named on the same ${model.rows.length} questions`
            : `${model.engines[engine].label}: out of ${model.rows.filter((r) => r.cells[engine] !== null).length} questions`,
        sources: staff ? "Where the answers read about the business" : "",
    };
    const probes = inputs.probes ?? [];
    const googleAiReadings = inputs.googleAiReadings ?? [];
    const staffControls = !staff ? null : tab === "questions" ? (
        // For checking a scan against a manual measurement (#3786).
        <PillButton
            size="sm"
            icon={DownloadIcon}
            disabled={probes.length === 0 && googleAiReadings.length === 0}
            title={probes.length === 0 && googleAiReadings.length === 0 ? "This scan has no per-question answers to export." : "One row per question and engine."}
            onClick={() => downloadQuestionsCsv(probes, businessName, model.capturedAt, googleAiReadings)}
        >
            Export CSV
        </PillButton>
    ) : canStage(analyseJob) ? (
        <CardRefresh job={analyseJob} spec={ANALYSE_REFRESH} />
    ) : null;

    return (
        <FoldScope value={staff ? "staff-visibility" : "merchant-visibility"}>
            {staff ? (
                <>
                    <PageSentence>
                        {/* The description, fixed; the figures below it
                            (#4026 C-FE-6). The staff chrome has one sentence
                            slot, so both live in it, told apart by weight. */}
                        <span className="block">{STAFF_VISIBILITY_DESCRIPTION}</span>
                        <span className={cn(TYPE.tc, "mt-1.5 block font-medium tabular-nums text-gray-700")}>{headSentence}</span>
                    </PageSentence>
                    <PageActions>
                        {seg}
                        {/* The server's automatic audit of the scan on screen
                            (#4242). Staff on a live business only: a client never
                            sees it and the endpoint is not called for one. */}
                        <ScanAuditBadge
                            assessmentId={readAssessmentId}
                            enabled={staff && !fixture && !!readAssessmentId}
                            scanAt={capturedAt}
                            questionText={(n) => model.rows.find((r) => r.id === n)?.text ?? null}
                            onOpenQuestion={(n) => {
                                const row = model.rows.find((r) => r.id === n);
                                if (row) openQuestion(row);
                            }}
                        />
                        {versions.length > 0 && (
                            <PillButton icon={HistoryIcon} onClick={() => openHistory(null)}>
                                Question history
                            </PillButton>
                        )}
                        {canReadGoogleAi && !googleAi.absent && (
                            <PillButton
                                icon={RefreshCwIcon}
                                busy={googleAi.starting || googleAi.running}
                                disabled={!!googleAi.unavailable}
                                onClick={googleAi.ask}
                                title={googleAi.unavailable ?? "Ask Google for its AI Overviews again. Several minutes, and it spends proxy bandwidth."}
                            >
                                {googleAi.running ? "Reading Google…" : "Read Google's AI again"}
                            </PillButton>
                        )}
                    </PageActions>
                </>
            ) : (
                <PageHead
                    over={businessName}
                    title="Visibility"
                    // Fixed, and the same sentence the rail's tooltip gives
                    // (#4026 C-FE-6). It carries the counting rule, because
                    // the client reads the same number as staff (#4025 FE-5).
                    sentence={CLIENT_HINT.questions}
                    // The redesign leaves the count to the tile below it.
                    status={MERCHANT_V2 ? undefined : headSentence}
                    // The redesign keeps the head's right for the history
                    // controls, as on Website; the engine filter moves down
                    // onto the figures it filters.
                    actions={
                        <>
                            {!MERCHANT_V2 && seg}
                            {versions.length > 0 && (
                                <PillButton icon={HistoryIcon} onClick={() => openHistory(null)}>
                                    Question history
                                </PillButton>
                            )}
                            {scanControl}
                        </>
                    }
                />
            )}
            {/* Website is a tab here, not a rail item: the rail stays at five. */}
            {!staff && MERCHANT_V2 && <VisibilityTabs value="questions" />}

            <div className="space-y-5">
                {!staff && MERCHANT_V2 && seg && <div className="-mt-1">{seg}</div>}
                {staff && model.instrumentWarning && (
                    // The one caveat that changes what the figures mean, so it
                    // sits above them, in the backend's own words.
                    <div className="flex items-start gap-2.5 rounded-2xl bg-amber-50 px-4 py-3 text-amber-900">
                        <AlertTriangleIcon aria-hidden size={16} className="mt-0.5 shrink-0" />
                        <div className="min-w-0">
                            <p className={cn(TYPE.tc, "m-0 font-semibold")}>How ChatGPT was measured</p>
                            <p className={cn(TYPE.tc, "m-0 mt-0.5 break-words")}>{model.instrumentWarning}</p>
                        </div>
                    </div>
                )}

                {setChange && (
                    <SetChangeBanner
                        staff={staff}
                        from={setChange.from}
                        to={setChange.to}
                        before={setScans[setChange.previousScanId]}
                        after={setScans[setChange.to.scans[0].assessmentId]}
                        onOpen={() => openHistory(setChange.to.n, setChange.from.n)}
                    />
                )}

                <VisTiles
                    model={model}
                    staff={staff}
                    engine={engine}
                    delta={delta}
                    onEngine={setEngine}
                    onToTable={() => toTable({ filter: null, ...leaveCell() })}
                    // Both roles open the full Search performance screen. A
                    // client with no Search Console has nothing there to open.
                    googleTo={staff || model.google.connected ? searchHref : undefined}
                    gscState={gscState}
                />

                {/* What a partial scan left out, under the count it explains
                    rather than in a banner above the title. Staff keep the
                    banner over the screen. */}
                {merchantScanNote && (
                    <p className={cn(TYPE.tc, "m-0 -mt-2 flex items-start gap-1.5 text-gray-500")}>
                        <InfoIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-gray-400" />
                        <span className="min-w-0">{merchantScanNote}</span>
                    </p>
                )}

                {staff && (googleAi.running || googleAi.note || googleAi.unavailable) && (
                    <p className={cn(TYPE.tc, "m-0", googleAi.note?.tone === "warn" ? "text-amber-800" : "text-gray-500")}>
                        {googleAi.running
                            ? "Asking Google for each of the questions. This takes several minutes; the By AI ring updates when it lands."
                            : googleAi.note?.text ?? googleAi.unavailable}
                    </p>
                )}

                {kinds.length > 0 ? (
                    // Side by side only from 1280px: at 1024 the chart's axis
                    // and the grid's heads were squeezed past reading.
                    <Bento layout="c2xl">
                        <NamedOverTime
                            model={merchantChart ?? shownModel}
                            engine={engine}
                            staff={staff}
                            right={trendRight}
                            versionOf={versionAt}
                            methodOf={methodAt}
                            currentVersion={latestVersion}
                            shownVersion={currentVersion}
                            onPickVersion={canPickVersion ? pickVersion : undefined}
                            onOpenVersion={openVersionHistory}
                            scanOf={scanAt}
                            notedOn={notedOn}
                        />
                        <TypeGrid model={model} kinds={kinds} engine={engine} staff={staff} onCell={onCell} />
                    </Bento>
                ) : (
                    // A set from before the types has no grid, and the trend
                    // takes the row rather than leaving half of it empty.
                    <NamedOverTime
                        model={merchantChart ?? shownModel}
                        engine={engine}
                        staff={staff}
                        right={trendRight}
                        versionOf={versionAt}
                        methodOf={methodAt}
                        currentVersion={latestVersion}
                        shownVersion={currentVersion}
                        onPickVersion={canPickVersion ? pickVersion : undefined}
                        onOpenVersion={openVersionHistory}
                        scanOf={scanAt}
                        notedOn={notedOn}
                    />
                )}

                <ReportCard
                    ref={reportRef}
                    tab={tab}
                    counts={{ questions: model.rows.length, competitors: rivals.length, sources: model.sources.rows.length }}
                    onTab={(t) => update({ tab: t === "questions" ? null : t }, true)}
                    // The merchant's Questions tab shows its key up here, in the
                    // header's empty corner, instead of under the table.
                    hint={!staff && MERCHANT_V2 && tab === "questions" ? <span ref={setKeySlot} className="contents" /> : tabHint[tab]}
                    // Which version's questions every tab shows, said once here
                    // rather than on each row (Jack, 22 Oct 2026).
                    marker={versions.length > 1 && currentVersion !== null ? (
                        <VersionMarker
                            name={staff || !MERCHANT_V2 ? versionName(currentVersion) : `Set ${currentVersion}`}
                            checked={onNewest ? null : dayMonth(inputs.capturedAt)}
                            onBack={onNewest ? undefined : backToNewest}
                        />
                    ) : undefined}
                    controls={staffControls}
                >
                    {tab === "questions" && (
                        <QuestionsTab
                            model={model}
                            staff={staff}
                            keySlot={!staff && MERCHANT_V2 ? keySlot : null}
                            engine={engine}
                            filters={filters}
                            filter={activeFilter.key}
                            rows={model.rows.filter(activeFilter.test)}
                            page={page}
                            // Per the engine on screen (#3947): one flag for the
                            // whole table described whichever instrument the run
                            // happened to use, not the column being read.
                            notStored={!answers.storedFor(engine)}
                            onFilter={setFilter}
                            onPage={(p) => update({ page: p > 1 ? String(p) : null })}
                            onOpen={openQuestion}
                            onFix={onOpenTask}
                            // The place beside the check comes from the index
                            // the drawer already fetches, so the table costs
                            // no extra request.
                            rankFor={(row, e) => answers.rankFor(row.key, e)}
                            trendFor={(row, e) => cellTrend(row, e, model.engines[e].label, trendScans.scans, trendScans.nowInstrument)}
                            newFor={prevSetScanId ? setNewFor ?? undefined : newSinceFor(model.rows, trendScans.scans) ?? undefined}
                            newVersion={setNewFor && currentVersion !== null ? (staff || !MERCHANT_V2 ? versionName(currentVersion) : `Set ${currentVersion}`) : undefined}
                            searchNotice={
                                <SearchesEmptyNotice
                                    staff={staff}
                                    empty={model.searchEmpty}
                                    anySearch={!model.searchEmpty.wholeScan}
                                    searchJob={staff && canStage(searchJob) ? searchJob : undefined}
                                />
                            }
                            footer={
                                <UncoveredSearches
                                    staff={staff}
                                    block={inputs.searchQuestions}
                                    businessId={staff ? writableBusinessId : undefined}
                                    searchJob={staff && canStage(searchJob) ? searchJob : undefined}
                                />
                            }
                        />
                    )}
                    {tab !== "questions" && canStage(analyseJob) && (analyseJob.running || analyseJob.note) && (
                        <div className="space-y-1 border-t border-gray-100 px-5 py-3">
                            <RefreshProgress job={analyseJob} runningText={ANALYSE_REFRESH.running} />
                        </div>
                    )}
                    {tab === "competitors" && (
                        <CompetitorsTab
                            model={model}
                            rivals={rivals}
                            engine={engine}
                            staff={staff}
                            businessName={businessName}
                            priorScan={priorScan}
                            selected={params.get("rival")}
                            onSelect={(name) => update({ rival: name })}
                            onOpen={openQuestion}
                            onLostTo={(name) => toTable({ filter: lostFilter(name), from: null })}
                            onFix={(taskId) => (taskId ? onOpenTask(taskId) : onNavigate("todo"))}
                        />
                    )}
                    {tab === "sources" && (
                        <SourcesTab model={model} staff={staff} engine={engine} onOpen={(row) => update({ source: row.domain, q: null }, true)} />
                    )}
                </ReportCard>
            </div>

            <QuestionDrawer
                // A new business or check remounts the drawer, so no open edit or correction carries over to it.
                key={`${businessId}:${fixture?.assessmentId ?? writableAssessmentId ?? "none"}`}
                row={openRow}
                model={model}
                staff={staff}
                businessName={businessName}
                // H21's block on the scan, when the server sends one.
                namedElsewhere={(assessment?.results as { namedElsewhere?: NamedElsewhereBlock | null } | null | undefined)?.namedElsewhere ?? null}
                answers={answers}
                assessmentId={fixture ? undefined : readAssessmentId}
                backTo={source?.domain ?? null}
                // The drawer opens on the engine this page is filtered to.
                engine={engine}
                onClose={() => update({ q: null })}
                onFix={onOpenTask}
                fixAssessmentId={staff && !fixture ? writableAssessmentId : undefined}
                onCorrected={onRefreshed}
                // Live only: the sample cannot recount its score, so it offers no correction.
                verdictTarget={staff && !fixture && VERDICT_FIX_LIVE && writableAssessmentId ? { assessmentId: writableAssessmentId, sample: false } : undefined}
            />
            <CitesDrawer
                source={openRow ? null : source}
                model={model}
                staff={staff}
                onClose={() => update({ source: null })}
                onOpenQuestion={openFromSource}
            />
            {historyOpen && (
                <QuestionHistory
                    open
                    staff={staff}
                    versions={versions}
                    selected={historyPicked}
                    compareWith={params.get("vs") ? Number(params.get("vs")) : null}
                    currentVersion={currentVersion}
                    scans={setScans}
                    onSelect={(n) => update({ sets: String(n), vs: null })}
                    onCompare={(n) => update({ vs: String(n) })}
                    onClose={() => update({ sets: null, vs: null })}
                    nextCheckAt={nextCheckAt}
                    // Offered once the backend's one-call version route is live; a sample shows it now.
                    onPublish={staff && (!!fixture || VERSION_PUBLISH_LIVE) ? setVersionPublished : undefined}
                />
            )}
            {canReadGoogleAi && (
                <RefreshConfirmDialog
                    job={googleAi}
                    busy={googleAi.starting}
                    title="Read Google's AI Overviews?"
                    cards={["By AI", "How this was measured"]}
                    confirmLabel="Read Google"
                >
                    <p className="my-0">
                        This searches Google for each of the {model.rows.length} questions in a real browser and reads the AI
                        Overview it writes above the results.
                    </p>
                    <p className="my-0">
                        It takes several minutes and spends proxy bandwidth. Nothing else moves: the other engines, the
                        questions and the last scan date stay as they are. Only the Google&rsquo;s AI figure changes, with
                        its own read date.
                    </p>
                </RefreshConfirmDialog>
            )}
        </FoldScope>
    );
}
